import React, { useMemo } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { appInsights } from '../context/applicationInsights/ApplicationInsightsService';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useNotificationContext } from '../global/hooks/useNotificationContext';
import { HTTPError } from 'ky';
import { graphConfig } from '../config/authConfig';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ApiResponse } from '../Model/ApiResponse';
import { GearNotificationProps } from './common/GearNotification';

interface QueryProviderWrapperProps {
  children: React.ReactNode;
}

const shouldIgnoreError = (errorResponse: Response): boolean => {
  return errorResponse.status === 403 && errorResponse.url.includes(graphConfig.graphSharesEndpoint);
};

const getCorrelationId = async (errorResponse: Response): Promise<string | null> => {
  try {
    const response = (await errorResponse.json()) as ApiResponse;
    return response.errorDetails?.operationId ?? null;
  } catch {
    return null;
  }
};

const handle500Error = async (
  errorResponse: Response,
  showToast: (props: Omit<GearNotificationProps, 'show'>) => void
): Promise<void> => {
  const errorMessage = 'Something bad happened';
  const correlationId = await getCorrelationId(errorResponse);
  const fullMessage = correlationId ? `${errorMessage} (Correlation ID: ${correlationId})` : errorMessage;

  showToast({ type: 'error', message: fullMessage });
  appInsights.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

const handle4xxError = (error: Error): void => {
  const errorMessage = `Something went wrong: ${error.message}`;
  appInsights.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

const handleHTTPError = async (
  error: HTTPError,
  showToast: (props: Omit<GearNotificationProps, 'show'>) => void
): Promise<void> => {
  const { response: errorResponse } = error;

  if (!errorResponse) {
    return;
  }

  if (shouldIgnoreError(errorResponse)) {
    return;
  }

  if (errorResponse.status === 500) {
    await handle500Error(errorResponse, showToast);
    return;
  }

  if (errorResponse.status >= 400 && errorResponse.status < 500) {
    handle4xxError(error);
  }
};

const handleGenericError = (query: any, showToast: (props: Omit<GearNotificationProps, 'show'>) => void): void => {
  const errorMessage = `Something went wrong for query: ${query.queryKey}`;
  showToast({ type: 'error', message: errorMessage });
  appInsights.trackException({
    error: new Error(`FRONTEND: ${errorMessage}`),
    severityLevel: SeverityLevel.Error
  });
};

export const QueryProviderWrapper: React.FC<QueryProviderWrapperProps> = ({ children }) => {
  const { showToast } = useNotificationContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 2000
          }
        },
        queryCache: new QueryCache({
          onError: async (error, query) => {
            if (!query.state.error) {
              return;
            }

            if (error instanceof Error) {
              await handleHTTPError(error as HTTPError, showToast);
            } else {
              handleGenericError(query, showToast);
            }
          }
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (mutation.options.onError) {
              return;
            }
            if (error instanceof Error) {
              const errorMessage = `Something went wrong: ${error.message}`;
              showToast({ type: 'error', message: errorMessage });
              appInsights.trackException({
                error: new Error(`FRONTEND: ${errorMessage}`),
                severityLevel: SeverityLevel.Error
              });
            } else {
              const errorMessage = `Something went wrong: ${error}`;
              appInsights.trackException({
                error: new Error(`FRONTEND: ${errorMessage}`),
                severityLevel: SeverityLevel.Error
              });
            }
          }
        })
      }),
    [showToast]
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* By default, React Query Devtools are only included in bundles when process.env.NODE_ENV === 'development' */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
